
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText } from "lucide-react";
import { examsAPI, examQuestionsAPI } from "@/services/api";
import { useToast } from "@/hooks/use-toast";
import SelectedQuestionsModal from "./SelectedQuestionsModal";
import AddQuestionModal from "./AddQuestionModal";
import ManualQuestionModal from "./ManualQuestionModal";
import QuestionBankSelectionModal from "./QuestionBankSelectionModal";
import BasicInfoTab from "./exam-modal/BasicInfoTab";
import QuestionsTab from "./exam-modal/QuestionsTab";
import SecurityTab from "./exam-modal/SecurityTab";
import AdvancedTab from "./exam-modal/AdvancedTab";

const AddExamModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    totalTime: 60,
    language: "English",
    passPercentage: 50,
    isTemplate: false,
    instructions: "",
    warningsAllowed: 0,
    proctoring: {
      enabled: false,
      captureImages: false,
      trackTabSwitching: false,
      faceDetection: false,
      captureFrequency: 10000,
    },
  });

  const [currentTab, setCurrentTab] = useState("basic");

  // ... keep existing code (state variables and queries)
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [questionPage, setQuestionPage] = useState(1);
  const [questionFilters, setQuestionFilters] = useState({
    search: "",
    questionBankIds: [],
    difficulties: [],
    tags: "",
  });

  const [autoSelections, setAutoSelections] = useState([]);
  const [newAutoSelection, setNewAutoSelection] = useState({
    questionBankIds: [],
    difficulties: {
      easy: 0,
      medium: 0,
      hard: 0,
    },
  });

  const [showSelectedQuestions, setShowSelectedQuestions] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showManualQuestionModal, setShowManualQuestionModal] = useState(false);
  const [showQuestionBankModal, setShowQuestionBankModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const {
    data: questionsData,
    isLoading: questionsLoading,
    refetch: refetchQuestions,
  } = useQuery({
    queryKey: ["exam-questions", questionPage, questionFilters],
    queryFn: () => {
      return examQuestionsAPI.getQuestions({
        page: questionPage,
        limit: 10,
        questionBankIds: questionFilters.questionBankIds,
        difficulties: questionFilters.difficulties,
        tags: questionFilters.tags,
        search: questionFilters.search,
      });
    },
    enabled: isOpen,
    keepPreviousData: true,
  });

  const { data: categoriesData } = useQuery({
    queryKey: ["exam-question-categories"],
    queryFn: () => examQuestionsAPI.getCategories(),
    enabled: isOpen,
  });

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      totalTime: 60,
      language: "English",
      passPercentage: 50,
      isTemplate: false,
      instructions: "",
      warningsAllowed: 0,
      proctoring: {
        enabled: false,
        captureImages: false,
        trackTabSwitching: false,
        faceDetection: false,
        captureFrequency: 10000,
      },
    });
    setSelectedQuestions([]);
    setAutoSelections([]);
    setQuestionPage(1);
    setQuestionFilters({
      search: "",
      questionBankIds: [],
      difficulties: [],
      tags: "",
    });
    setNewAutoSelection({
      questionBankIds: [],
      difficulties: {
        easy: 0,
        medium: 0,
        hard: 0,
      },
    });
    setCurrentTab("basic");
  };

  // ... keep existing code (useEffect, handlers, and other functions)
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  const handleQuestionToggle = (question) => {
    setSelectedQuestions((prev) => {
      const exists = prev.find((q) => q._id === question._id);
      if (exists) {
        return prev.filter((q) => q._id !== question._id);
      } else {
        return [...prev, question];
      }
    });
  };

  const handleQuestionRemove = (questionId) => {
    setSelectedQuestions((prev) => prev.filter((q) => q._id !== questionId));
  };

  // Handle Add Question Modal Actions
  const handleAddQuestions = () => {
    setShowAddQuestionModal(true);
  };

  const handleAddQuestionManually = () => {
    setShowAddQuestionModal(false);
    setShowManualQuestionModal(true);
  };

  const handleAddFromQuestionBank = () => {
    setShowAddQuestionModal(false);
    setShowQuestionBankModal(true);
  };

  // Handle questions added from manual creation
  const handleManualQuestionsAdded = (newQuestions) => {
    setSelectedQuestions((prev) => {
      const existingIds = prev.map((q) => q._id);
      const uniqueNewQuestions = newQuestions.filter(
        (q) => !existingIds.includes(q._id)
      );
      return [...prev, ...uniqueNewQuestions];
    });
    setShowManualQuestionModal(false);
  };

  // Handle questions selected from question bank
  const handleQuestionBankSelection = (questions) => {
    setSelectedQuestions((prev) => {
      const existingIds = prev.map((q) => q._id);
      const uniqueNewQuestions = questions.filter(
        (q) => !existingIds.includes(q._id)
      );
      return [...prev, ...uniqueNewQuestions];
    });
    setShowQuestionBankModal(false);
  };

  const handleAutoSelectionAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (newAutoSelection.questionBankIds.length === 0) {
      toast({
        title: "Invalid Selection",
        description: "Please select at least one question bank",
        variant: "destructive",
      });
      return;
    }

    const totalQuestions =
      (newAutoSelection.difficulties.easy || 0) +
      (newAutoSelection.difficulties.medium || 0) +
      (newAutoSelection.difficulties.hard || 0);

    if (totalQuestions === 0) {
      toast({
        title: "Invalid Selection",
        description: "Please specify the number of questions needed",
        variant: "destructive",
      });
      return;
    }

    setAutoSelections((prev) => [
      ...prev,
      { ...newAutoSelection, id: Date.now() },
    ]);
    setNewAutoSelection({
      questionBankIds: [],
      difficulties: {
        easy: 0,
        medium: 0,
        hard: 0,
      },
    });
  };

  const handleAutoSelectionRemove = (id) => {
    setAutoSelections((prev) => prev.filter((sel) => sel.id !== id));
  };

  const handleAutoSelectQuestions = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (autoSelections.length === 0) {
      toast({
        title: "No Auto Selections",
        description: "Please add at least one auto selection criteria",
        variant: "destructive",
      });
      return;
    }

    try {
      const response = await examQuestionsAPI.autoSelect(autoSelections);
      const autoSelectedQuestions = response.data.selectedQuestions;

      setSelectedQuestions((prev) => {
        const existingIds = prev.map((q) => q._id);
        const newQuestions = autoSelectedQuestions.filter(
          (q) => !existingIds.includes(q._id)
        );
        return [...prev, ...newQuestions];
      });

      setAutoSelections([]);

      toast({
        title: "Questions Auto-Selected",
        description: `${autoSelectedQuestions.length} questions were automatically selected`,
      });
    } catch (error) {
      toast({
        title: "Auto Selection Failed",
        description:
          error.response?.data?.message || "Failed to auto-select questions",
        variant: "destructive",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({
        title: "Validation Error",
        description: "Exam name is required",
        variant: "destructive",
      });
      return;
    }

    if (selectedQuestions.length < 2) {
      toast({
        title: "Validation Error",
        description: "Please select at least 2 questions",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await examsAPI.create({
        ...formData,
        questions: selectedQuestions.map((q) => q._id),
      });

      toast({
        title: "Success",
        description: "Exam created successfully",
      });

      onSuccess();
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to create exam",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubCategories = (categoryName) => {
    if (!categoriesData?.data?.categories) return [];
    const category = categoriesData.data.categories.find(
      (cat) => cat.name === categoryName
    );
    return category?.subCategories || [];
  };

  const isExamNameEmpty = !formData.name.trim();
  const canShowCreateButton = currentTab === "advanced";

  // ... keep existing code (return JSX)
  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-[95vw] h-[95vh] max-w-none flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create New Exam
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto pr-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="basic">Basic Info</TabsTrigger>
                  <TabsTrigger value="questions" disabled={isExamNameEmpty}>
                    Questions
                  </TabsTrigger>
                  <TabsTrigger value="security" disabled={isExamNameEmpty}>
                    Security
                  </TabsTrigger>
                  <TabsTrigger value="advanced" disabled={isExamNameEmpty}>
                    Advanced
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="basic" className="space-y-4">
                  <BasicInfoTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="questions" className="space-y-4">
                  <QuestionsTab
                    selectedQuestions={selectedQuestions}
                    setShowSelectedQuestions={setShowSelectedQuestions}
                    questionFilters={questionFilters}
                    setQuestionFilters={setQuestionFilters}
                    questionPage={questionPage}
                    setQuestionPage={setQuestionPage}
                    questionsData={questionsData}
                    questionsLoading={questionsLoading}
                    categoriesData={categoriesData}
                    handleQuestionToggle={handleQuestionToggle}
                    getSubCategories={getSubCategories}
                    newAutoSelection={newAutoSelection}
                    setNewAutoSelection={setNewAutoSelection}
                    autoSelections={autoSelections}
                    setAutoSelections={setAutoSelections}
                    handleAutoSelectionAdd={handleAutoSelectionAdd}
                    handleAutoSelectionRemove={handleAutoSelectionRemove}
                    handleAutoSelectQuestions={handleAutoSelectQuestions}
                    onAddQuestions={handleAddQuestions}
                  />
                </TabsContent>

                <TabsContent value="security" className="space-y-4">
                  <SecurityTab formData={formData} setFormData={setFormData} />
                </TabsContent>

                <TabsContent value="advanced" className="space-y-4">
                  <AdvancedTab formData={formData} setFormData={setFormData} />
                </TabsContent>
              </Tabs>
            </form>
          </div>

          <div className="flex justify-end space-x-2 pt-4 border-t flex-shrink-0 mt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {canShowCreateButton && (
              <Button
                type="button"
                disabled={isSubmitting}
                onClick={handleSubmit}
              >
                {isSubmitting ? "Creating..." : "Create Exam"}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <SelectedQuestionsModal
        isOpen={showSelectedQuestions}
        onClose={() => setShowSelectedQuestions(false)}
        selectedQuestions={selectedQuestions}
        onRemoveQuestion={handleQuestionRemove}
      />

      <AddQuestionModal
        isOpen={showAddQuestionModal}
        onClose={() => setShowAddQuestionModal(false)}
        onAddManually={handleAddQuestionManually}
        onAddFromQuestionBank={handleAddFromQuestionBank}
      />

      <ManualQuestionModal
        isOpen={showManualQuestionModal}
        onClose={() => setShowManualQuestionModal(false)}
        eligibleCandidates={[]} // Empty for exam creation context
        aiModuleId={null} // Not applicable for exam creation
        onQuestionsAdded={handleManualQuestionsAdded}
      />

      <QuestionBankSelectionModal
        isOpen={showQuestionBankModal}
        onClose={() => setShowQuestionBankModal(false)}
        onQuestionsSelected={handleQuestionBankSelection}
      />
    </>
  );
};

export default AddExamModal;